// ─── ProjectMapView ─────────────────────────────────────────────────────────
// "Project Map" dashboard tab. Renders a full-bleed Leaflet map with one
// coloured pin per site that has GPS coordinates. The Edit button in the
// top-right corner opens MapEditorModal where the user can tweak pin colours,
// pick the base tile layer, and set the default centre + zoom.
//
// Data:
//   - sites prop comes from DashboardPage's projectSites state (the same
//     /api/project/{id}/sites/ payload used by the sidebar).
//   - Map configuration (pin colours, tile layer, centre + zoom) is read
//     from and persisted to localStorage by mapConfig helpers, keyed per
//     project so each project keeps its own map setup.
//
// Layer-switching:
//   - The TileLayer's `key` prop is bound to the active tile layer key so
//     React fully remounts the layer on switch — the simplest way to swap
//     URL/attribution/maxZoom together without leaking state.
//
// Phase 2 (not in this commit): BYO Google/Esri API key from Account
// settings — at render time we'd swap in a Google/Esri sub-renderer based
// on the user's chosen provider. Today: OSM, OpenTopoMap, and the free
// Esri World Imagery layer (no key required).
// ────────────────────────────────────────────────────────────────────────────

import { useMemo, useState, useEffect } from 'react'
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet'
import { Pencil, MapPin } from 'lucide-react'
import MapEditorModal from './MapEditorModal'
import {
    SITE_TYPES,
    getTileLayer,
    inferSiteType,
    loadMapConfig,
    saveMapConfig,
} from './mapConfig'

// Imperative helper: when the saved config has no explicit centre, fit the
// map to the bounding box of all site markers. Falls back to a global view
// if the project has no geocoded sites at all.
function FitBoundsOnce({ sites, configCenter }) {
    const map = useMap()
    useEffect(() => {
        // Honour an explicit centre if the user has saved one.
        if (configCenter && Number.isFinite(configCenter.lat) && Number.isFinite(configCenter.lng)) {
            map.setView([configCenter.lat, configCenter.lng], configCenter.zoom || 8)
            return
        }
        const points = sites
            .filter((s) => Number.isFinite(s.latitude) && Number.isFinite(s.longitude))
            .map((s) => [s.latitude, s.longitude])
        if (points.length === 0) {
            map.setView([20, 0], 2)
        } else if (points.length === 1) {
            map.setView(points[0], 10)
        } else {
            map.fitBounds(points, { padding: [40, 40] })
        }
    // Re-fit when the set of sites changes — but NOT when configCenter
    // changes mid-render, since we always defer to the saved centre when
    // present.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sites.length])
    return null
}

// ── Site marker popup ───────────────────────────────────────────────────────
// Matches the legacy Symfony popup layout: site name in the header, a
// Data / Gallery tab strip, and (under Data) a list of every device on the
// site with the device's last-contact timestamp underneath — same shape
// the customer saw on the old map.
//
// The Gallery tab currently shows a placeholder; per-site photo filtering
// will be wired in a follow-up once the gallery API supports a `site=` filter.
// Until then, hiding the tab would diverge from the legacy parity the user
// asked for, so it's present but flagged.
function fmtTimestamp(iso) {
    if (!iso) return ''
    try {
        const d = new Date(iso)
        if (Number.isNaN(d.getTime())) return ''
        // YYYY-MM-DD HH:MM:SS, matching the legacy popup format.
        const pad = (n) => String(n).padStart(2, '0')
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    } catch {
        return ''
    }
}

function SiteMarkerPopup({ site, bucket, color }) {
    const [tab, setTab] = useState('data')
    const devices = Array.isArray(site.devices) ? site.devices : []

    return (
        <div className="text-xs min-w-[200px]">
            {/* Header — site name + site-type chip */}
            <div className="font-semibold text-gray-800 text-sm mb-1">{site.name}</div>
            <div className="flex items-center gap-2 mb-2">
                <span
                    className="inline-block px-1.5 py-0.5 text-[10px] uppercase rounded font-medium"
                    style={{ background: color + '22', color }}
                >
                    {SITE_TYPES.find((t) => t.key === bucket)?.label || bucket}
                </span>
                {site.site_code && (
                    <span className="text-[10px] text-gray-400">Code: {site.site_code}</span>
                )}
            </div>

            {/* Tab strip */}
            <div className="flex border-b border-gray-200 -mx-1 mb-2">
                <button
                    type="button"
                    onClick={() => setTab('data')}
                    className={`px-3 py-1 text-xs font-medium border-b-2 transition-colors ${
                        tab === 'data'
                            ? 'border-[#358ECD] text-[#358ECD]'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                    Data
                </button>
                <button
                    type="button"
                    onClick={() => setTab('gallery')}
                    className={`px-3 py-1 text-xs font-medium border-b-2 transition-colors ${
                        tab === 'gallery'
                            ? 'border-[#358ECD] text-[#358ECD]'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                    Gallery
                </button>
            </div>

            {/* Tab content */}
            {tab === 'data' && (
                <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {devices.length === 0 && (
                        <div className="text-gray-400 italic">No devices on this site.</div>
                    )}
                    {devices.map((d) => (
                        <div key={d.id} className="leading-tight">
                            <div className="font-semibold text-gray-800">
                                {d.name || d.uid || `Device ${d.id}`}
                            </div>
                            {d.last_contact_time ? (
                                <div className="text-gray-500 text-[11px]">
                                    {fmtTimestamp(d.last_contact_time)}
                                </div>
                            ) : (
                                <div className="text-gray-400 text-[11px] italic">No contact yet</div>
                            )}
                        </div>
                    ))}
                    {/* Coordinates footnote — handy for QA */}
                    <div className="pt-1 border-t border-gray-100 text-[10px] text-gray-400">
                        {site.latitude.toFixed(5)}, {site.longitude.toFixed(5)}
                    </div>
                </div>
            )}

            {tab === 'gallery' && (
                <div className="text-gray-500 italic text-[11px] py-2">
                    Per-site photo gallery coming soon.
                </div>
            )}
        </div>
    )
}

export default function ProjectMapView({ sites = [], projectId }) {
    const [config, setConfig] = useState(() => loadMapConfig(projectId))
    const [editorOpen, setEditorOpen] = useState(false)

    // Reload config when the active project changes so opening Project Map
    // on a different project doesn't show another project's pin colours.
    useEffect(() => {
        setConfig(loadMapConfig(projectId))
    }, [projectId])

    // Only sites with usable coordinates can be rendered as pins.
    const geocodedSites = useMemo(
        () => sites.filter((s) => Number.isFinite(Number(s.latitude)) && Number.isFinite(Number(s.longitude)))
                   .map((s) => ({ ...s, latitude: Number(s.latitude), longitude: Number(s.longitude) })),
        [sites]
    )

    const tile = getTileLayer(config.tileLayer)
    const explicitCenter =
        Number.isFinite(config.centerLat) && Number.isFinite(config.centerLng)
            ? { lat: config.centerLat, lng: config.centerLng, zoom: config.zoom }
            : null

    const initialCenter = explicitCenter
        ? [explicitCenter.lat, explicitCenter.lng]
        : geocodedSites.length > 0
            ? [geocodedSites[0].latitude, geocodedSites[0].longitude]
            : [20, 0]
    const initialZoom = explicitCenter?.zoom ?? (geocodedSites.length > 0 ? 8 : 2)

    const handleSave = (next) => {
        setConfig(next)
        saveMapConfig(projectId, next)
        setEditorOpen(false)
    }

    // Empty state — project has sites but none of them have coordinates.
    // Still render the map so the Edit button is reachable.
    const showNoPinsHint = sites.length > 0 && geocodedSites.length === 0

    return (
        <div className="relative h-[calc(100vh-200px)] min-h-[500px] bg-white rounded shadow-sm overflow-hidden">
            {/* Edit button — top-right floating control.
                z-index has to clear Leaflet's pane stack (tilePane 200,
                overlayPane 400, markerPane 600, popupPane 700, control
                container 1000). z-[1100] puts the button + the no-pins
                banner above every layer the map can draw on top of. */}
            <button
                type="button"
                onClick={() => setEditorOpen(true)}
                title="Edit map settings"
                className="absolute top-3 right-3 z-[1100] flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded shadow text-sm text-gray-700 hover:bg-gray-50"
            >
                <Pencil className="w-3.5 h-3.5" />
                <span>Edit</span>
            </button>

            {showNoPinsHint && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1100] flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 shadow-sm">
                    <MapPin className="w-3.5 h-3.5" />
                    No sites in this project have latitude / longitude set.
                </div>
            )}

            <MapContainer
                // Key on tile layer so a layer change in the editor remounts
                // the whole map — the cheapest way to ensure attribution,
                // maxZoom and URL all swap atomically.
                key={`${tile.key}-${projectId || 'none'}`}
                center={initialCenter}
                zoom={initialZoom}
                style={{ width: '100%', height: '100%' }}
                scrollWheelZoom
            >
                <TileLayer
                    url={tile.url}
                    attribution={tile.attribution}
                    maxZoom={tile.maxZoom}
                />

                <FitBoundsOnce sites={geocodedSites} configCenter={explicitCenter} />

                {geocodedSites.map((site) => {
                    const bucket = inferSiteType(site)
                    const color = config.pinColors[bucket] || '#358ECD'
                    return (
                        <CircleMarker
                            key={site.id}
                            center={[site.latitude, site.longitude]}
                            radius={9}
                            pathOptions={{
                                color: '#ffffff',
                                weight: 2,
                                fillColor: color,
                                fillOpacity: 0.95,
                            }}
                        >
                            <Popup minWidth={220} maxWidth={280}>
                                <SiteMarkerPopup site={site} bucket={bucket} color={color} />
                            </Popup>
                        </CircleMarker>
                    )
                })}
            </MapContainer>

            <MapEditorModal
                open={editorOpen}
                initialConfig={config}
                onSave={handleSave}
                onCancel={() => setEditorOpen(false)}
            />
        </div>
    )
}
